/**
 * ReferAFriendCard — the Refer-a-Friend surface (TS-016).
 *
 * Implements TS-016 (T4): code + copy, link + copy, client-side QR, invite
 * message preview, WhatsApp/Telegram/Facebook share buttons, a progressive
 * Web Share button, and an Instagram note (no fake one-tap post button).
 * Mounted as the LAST tab of ParentDashboardPage, after Plan & Coins (T5).
 *
 * Enforces:
 *   AC-TS016.1 — code (copy), link (copy), client QR, NO stats/counts.
 *   AC-TS016.3 — WhatsApp/Telegram/Facebook are plain <a target="_blank">
 *                share-intent anchors; NO third-party SDK/script anywhere on
 *                this component (BR-TS016.1).
 *   AC-TS016.4 — Web Share button renders ONLY when canWebShare() is true at
 *                mount (progressive enhancement); explicit buttons + copy
 *                always work regardless.
 *   AC-TS016.5 — Instagram is covered by Web Share + QR + copy-link only; no
 *                button claims a one-tap Instagram web post.
 *   AC-TS016.6 — non-commercial tone: no "earn"/earning/commission/cash
 *                framing anywhere in this file's copy; the shared text is the
 *                backend-configurable `referral.message`, not a hardcoded string.
 *
 * KB TO-LL-036 (enforcement point: the error branch below): getMyReferral's
 * errors propagate through useMyReferral untouched — this component renders
 * an explicit ErrorState with a working retry rather than silently rendering
 * an empty/broken card.
 * KB TO-LL-003 (enforcement point: destructuring `referral`): consumes
 * `MyReferral.code/link/message` verbatim from the hook/type — no renaming,
 * no client-side re-derivation of the link host (the backend-built `link` is
 * used as-is, never reconstructed here).
 */

import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, Share2, MessageCircle, Send, QrCode } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { useMyReferral } from '@/hooks/useMyReferral';
import {
  whatsappShareUrl,
  telegramShareUrl,
  facebookShareUrl,
  canWebShare,
  webShare,
  copyToClipboard,
} from '@/lib/shareIntents';
import { cn } from '@/lib/utils';

/** How long the copy button shows its "Copied" confirmation state, in ms. */
const COPY_CONFIRM_MS = 1500;

export function ReferAFriendCard() {
  const { referral, isLoading, error, refetch } = useMyReferral();

  // Snapshot Web Share support once at mount (AC-TS016.4) so tests can stub
  // navigator.share before render and reliably assert presence/absence.
  const [shareable] = useState(() => canWebShare());

  // Local copy-confirmation state — which field ('code' | 'link') last showed
  // a success confirmation, cleared after COPY_CONFIRM_MS.
  const [copiedField, setCopiedField] = useState<'code' | 'link' | null>(null);

  const handleCopy = async (field: 'code' | 'link', value: string) => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopiedField(field);
      window.setTimeout(() => setCopiedField(null), COPY_CONFIRM_MS);
    }
  };

  const handleWebShare = async () => {
    if (!referral) return;
    try {
      await webShare({ title: 'ThinkOtter', text: referral.message, url: referral.link });
    } catch {
      // AbortError (user closed the native share sheet) is expected UX, not
      // an application error. Any other rejection is already surfaced to
      // the user by the OS share sheet itself — nothing further to do here.
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading your invite..." />;
  }

  if (error || !referral) {
    return (
      <ErrorState
        title="Could not load your invite"
        message={
          (error as { message?: string } | null)?.message ??
          'We could not load your referral invite right now. Please try again.'
        }
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="rounded-3xl border border-otter-sand bg-white p-5 shadow-md">
      <p className="mb-1 flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wider text-pebbles-400">
        <span aria-hidden="true">🤝</span> Refer a Friend
      </p>
      <h2 className="font-display text-xl font-semibold text-otter-earth-800">
        Refer a Friend
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        Know a family who&apos;d love ThinkOtter? Share your invite link below.
      </p>
      <p className="mt-1 text-sm text-gray-500">
        {typeof referral.referrerCoinAmount === 'number'
          ? `Get ${referral.referrerCoinAmount} bonus ${
              referral.referrerCoinAmount === 1 ? 'coin' : 'coins'
            } for every friend who joins with your link.`
          : 'Get bonus coins for every friend who joins with your link.'}
      </p>

      {/* ── Code row ─────────────────────────────────────────────────────── */}
      <div className="mt-5 flex items-center justify-between gap-3 rounded-2xl border border-otter-sand bg-otter-paper px-4 py-3">
        <span className="font-mono text-base font-bold tracking-wide text-otter-earth-800">
          {referral.code}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label="Copy referral code"
          onClick={() => handleCopy('code', referral.code)}
          className="gap-1.5"
        >
          {copiedField === 'code' ? (
            <Check className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Copy className="h-4 w-4" aria-hidden="true" />
          )}
          {copiedField === 'code' ? 'Copied' : 'Copy code'}
        </Button>
      </div>

      {/* ── Link row ─────────────────────────────────────────────────────── */}
      <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-otter-sand bg-otter-paper px-4 py-3">
        <span className="truncate text-sm text-otter-earth-700">{referral.link}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label="Copy invite link"
          onClick={() => handleCopy('link', referral.link)}
          className="shrink-0 gap-1.5"
        >
          {copiedField === 'link' ? (
            <Check className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Copy className="h-4 w-4" aria-hidden="true" />
          )}
          {copiedField === 'link' ? 'Copied' : 'Copy link'}
        </Button>
      </div>

      {/* ── QR + message ─────────────────────────────────────────────────── */}
      <div className="mt-5 flex flex-col items-center gap-4 sm:flex-row sm:items-start">
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-otter-sand bg-white p-3">
          <QRCodeSVG value={referral.link} size={160} includeMargin />
          <p className="flex items-center gap-1 text-xs text-gray-500">
            <QrCode className="h-3.5 w-3.5" aria-hidden="true" /> Scan to open the invite link
          </p>
        </div>

        <div className="flex-1">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Invite message
          </p>
          <p className="rounded-2xl border border-otter-sand bg-otter-paper p-3 text-sm text-otter-earth-700">
            {referral.message}
          </p>
        </div>
      </div>

      {/* ── Share buttons ────────────────────────────────────────────────── */}
      <div className="mt-5">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Share via
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            href={whatsappShareUrl(referral.message)}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(buttonVariants({ variant: 'outline' }), 'gap-1.5')}
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" /> WhatsApp
          </a>
          <a
            href={telegramShareUrl(referral.link, referral.message)}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(buttonVariants({ variant: 'outline' }), 'gap-1.5')}
          >
            <Send className="h-4 w-4" aria-hidden="true" /> Telegram
          </a>
          <a
            href={facebookShareUrl(referral.link)}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(buttonVariants({ variant: 'outline' }), 'gap-1.5')}
          >
            <Share2 className="h-4 w-4" aria-hidden="true" /> Facebook
          </a>
          {shareable && (
            <Button type="button" variant="warm" onClick={handleWebShare} className="gap-1.5">
              <Share2 className="h-4 w-4" aria-hidden="true" /> Share...
            </Button>
          )}
        </div>

        {/* AC-TS016.5: Instagram via Web Share (if available) + QR + copy-link only —
            no button claims a one-tap Instagram web post. */}
        <p className="mt-3 text-xs text-gray-500">
          For Instagram, use Share (if available), scan the QR, or copy the link.
        </p>
      </div>
    </div>
  );
}
